import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem("token");
    console.log("[Auth] Initial token from localStorage:", t ? `${t.substring(0, 20)}...` : "null");
    return t;
  });
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        console.log("[Auth] Refreshing user with token...");
        const userData = await api.getMe(token);
        console.log("[Auth] User refreshed:", userData?.email);
        setUser(userData);
        return userData;
      } catch (error) {
        console.error("[Auth] Error refreshing user:", error);
        return null;
      }
    }
    return null;
  }, [token]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      console.log("[Auth] initAuth starting");
      console.log("[Auth] Token in state:", token ? "present" : "null");
      console.log("[Auth] Token in localStorage:", storedToken ? storedToken.substring(0, 20) + "..." : "null");
      
      const tokenToUse = token || storedToken;
      
      if (tokenToUse) {
        try {
          console.log("[Auth] Calling api.getMe to validate token...");
          const userData = await api.getMe(tokenToUse);
          console.log("[Auth] api.getMe success:", userData?.email);
          setUser(userData);
          if (!token && storedToken) {
            setToken(storedToken);
          }
        } catch (error) {
          console.error("[Auth] initAuth error - token invalid:", error.message || error);
          console.log("[Auth] Clearing invalid session...");
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      } else {
        console.log("[Auth] No token found, user not authenticated");
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.login(email, password);
    setToken(response.access_token);
    setUser(response.user);
    localStorage.setItem("token", response.access_token);
    return response;
  };

  // Login with existing token (for Microsoft auth)
  const loginWithToken = async (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    setToken(accessToken);
    setUser(userData);
    return { access_token: accessToken, user: userData };
  };

  const register = async (email, password, fullName, role) => {
    const response = await api.register(email, password, fullName, role);
    setToken(response.access_token);
    setUser(response.user);
    localStorage.setItem("token", response.access_token);
    return response;
  };

  const logout = () => {
    console.log("[Auth] Logout called - clearing session");
    console.trace("[Auth] Logout stack trace");
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
  };

  const isAdmin = user?.role === "admin";
  const isRDLead = user?.role === "rd_lead";
  const isOpenInnovationLead = user?.role === "open_innovation_lead";

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        loginWithToken,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
        isAdmin,
        isRDLead,
        isOpenInnovationLead,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
